import { useState, useRef, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { downloadICS, openGoogleCalendar, openOutlookCalendar } from "../utils/calendarExport";
import styles from "./AddToCalendar.module.css";
import logger from "../utils/logger";

export default function AddToCalendar({ jobs, variant = "default" }) {
  const [open, setOpen] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [feedUrl, setFeedUrl] = useState("");
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState("");
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isMultiple = Array.isArray(jobs) && jobs.length > 1;
  const jobList = Array.isArray(jobs) ? jobs : [jobs];

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [open]);

  const handleOption = (action) => {
    setOpen(false);
    action();
  };

  const handleSubscribe = async () => {
    setOpen(false);
    setShowSubscribe(true);
    if (feedUrl) return; // Already fetched
    setFeedError("");
    setFeedLoading(true);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "getCalendarFeedUrl");
      const result = await fn();
      setFeedUrl((result.data as { url: string }).url);
    } catch (err) {
      logger.error("Failed to get calendar feed URL:", err);
      setFeedError(err?.message || "Could not generate your calendar feed URL.");
    } finally {
      setFeedLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(feedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const btnClass = variant === "small" ? styles.triggerSmall : styles.trigger;

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        className={btnClass}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title="Add to calendar"
      >
        <svg className={styles.icon} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4H16a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h1.25V2.75A.75.75 0 015.75 2zM4 7.5h12V6H4v1.5zm0 1.5v7h12V9H4z" clipRule="evenodd" />
        </svg>
        {variant !== "small" && (isMultiple ? "Export to Calendar" : "Add to Calendar")}
        <svg className={styles.chevron} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          <button
            className={styles.option}
            role="menuitem"
            onClick={() => handleOption(() => downloadICS(jobList))}
          >
            <span className={styles.optionIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <span>
              <span className={styles.optionLabel}>Apple Calendar</span>
              <span className={styles.optionHint}>Downloads .ics file</span>
            </span>
          </button>

          {!isMultiple && (
            <button
              className={styles.option}
              role="menuitem"
              onClick={() => handleOption(() => openGoogleCalendar(jobList[0]))}
            >
              <span className={styles.optionIcon}>
                <svg viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </span>
              <span>
                <span className={styles.optionLabel}>Google Calendar</span>
                <span className={styles.optionHint}>Opens in browser</span>
              </span>
            </button>
          )}

          {!isMultiple && (
            <button
              className={styles.option}
              role="menuitem"
              onClick={() => handleOption(() => openOutlookCalendar(jobList[0]))}
            >
              <span className={styles.optionIcon}>
                <svg viewBox="0 0 24 24">
                  <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 01-.587.234h-8.55V6.577h8.55c.228 0 .422.078.587.234A.782.782 0 0124 7.387zM13.812 2.17v19.66L0 19.612V4.387l13.812-2.218z" />
                  <path fill="#fff" d="M9.482 8.632c-.69-.602-1.537-.903-2.544-.903-1.063 0-1.934.32-2.614.96-.68.64-1.02 1.46-1.02 2.46 0 1.033.33 1.87.99 2.512.66.64 1.508.962 2.544.962 1.048 0 1.916-.31 2.604-.93.688-.62 1.032-1.43 1.032-2.432 0-1.054-.33-1.907-.992-2.558v-.07zm-1.32 4.084c-.348.408-.816.612-1.404.612-.588 0-1.056-.21-1.404-.63-.348-.42-.522-.99-.522-1.71 0-.744.174-1.332.522-1.764.348-.432.816-.648 1.404-.648.588 0 1.056.21 1.404.63.348.42.522 1.002.522 1.746 0 .744-.174 1.344-.522 1.764z" />
                </svg>
              </span>
              <span>
                <span className={styles.optionLabel}>Outlook</span>
                <span className={styles.optionHint}>Opens in browser</span>
              </span>
            </button>
          )}

          <button
            className={styles.option}
            role="menuitem"
            onClick={() => handleOption(() => downloadICS(jobList))}
          >
            <span className={styles.optionIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </span>
            <span>
              <span className={styles.optionLabel}>Download .ics File</span>
              <span className={styles.optionHint}>Works with any calendar app</span>
            </span>
          </button>

          <div className={styles.divider} />

          <button
            className={styles.option}
            role="menuitem"
            onClick={handleSubscribe}
          >
            <span className={styles.optionIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 11a9 9 0 019 9" />
                <path d="M4 4a16 16 0 0116 16" />
                <circle cx="5" cy="19" r="1" fill="currentColor" />
              </svg>
            </span>
            <span>
              <span className={styles.optionLabel}>Subscribe to Calendar</span>
              <span className={styles.optionHint}>Auto-sync all your shifts</span>
            </span>
          </button>
        </div>
      )}

      {showSubscribe && (
        <div
          className={styles.subscribeOverlay}
          onClick={() => setShowSubscribe(false)}
          role="presentation"
          aria-hidden="true"
        >
          <div
            className={styles.subscribePanel}
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <div className={styles.subscribePanelHeader}>
              <h3 className={styles.subscribePanelTitle}>Subscribe to Calendar</h3>
              <button className={styles.closeBtn} onClick={() => setShowSubscribe(false)} aria-label="Close">
                <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <p className={styles.subscribeDesc}>
              Subscribe once and all your shifts will automatically appear on your calendar. Changes sync automatically.
            </p>

            {feedLoading ? (
              <div className={styles.subscribeLoading}>Generating your personal feed URL...</div>
            ) : feedUrl ? (
              <>
                <div className={styles.urlRow}>
                  <input
                    className={styles.urlInput}
                    type="text"
                    value={feedUrl}
                    readOnly
                    onFocus={(e) => e.target.select()}
                  />
                  <button className={styles.copyBtn} onClick={handleCopy}>
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>

                <div className={styles.instructions}>
                  <div className={styles.instructionGroup}>
                    <strong>Outlook (Desktop)</strong>
                    <p>File &rarr; Account Settings &rarr; Internet Calendars &rarr; New &rarr; paste URL</p>
                  </div>
                  <div className={styles.instructionGroup}>
                    <strong>Google Calendar</strong>
                    <p>Settings &rarr; Add calendar &rarr; From URL &rarr; paste URL</p>
                  </div>
                  <div className={styles.instructionGroup}>
                    <strong>Apple Calendar</strong>
                    <p>File &rarr; New Calendar Subscription &rarr; paste URL</p>
                  </div>
                </div>
              </>
            ) : (
              <div className={styles.subscribeLoading} role="alert">
                {feedError || "Failed to load feed URL. Please try again."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
